import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo} from 'react';
import {FiChevronDown} from 'react-icons/fi';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {DropdownCustom} from '@ui/molecules/dropdown-custom';
import {
  FeaturePageContent,
  FeaturePageHeader,
} from '@ui/molecules/feature-page-header';
import {useStoreContext} from '@store/store-context';
import PublicWall from './public-wall/public-wall';
import MyFeedback from './my-feedback/my-feedback';
import {FeedBackDirectReports} from './direct-reports/feedback-direct-reports';
import {FeedbackSidecar, FeedbackSidecarView} from './feedback-sidecar';
import {FeedbackRequest} from './feedback-items';
import {FeedbackRequestImpl} from './feedback-requests';
import {FeedbackController} from './feedback-controller';
import {normaliseRequestResponse} from './feedback-feed';
import {excludeDismissedFeedbackRequests} from './feedback-dismissed-requests';
import {
  ActionMenu,
  ActionMenuItem,
  FeedbackCreateButton,
  FeedbackPageHeading,
  FeedbackPageHeadingRow,
  FeedbackPageMain,
  FeedbackPageWrapper,
} from './feedback-page.styles';

type FeedbackTab = 'for-you' | 'sent' | 'praise';

const feedbackTabs: Array<{
  id: FeedbackTab;
  label: string;
  title: string;
  subtitle: string;
}> = [
  {
    id: 'for-you',
    label: 'For You',
    title: 'For You',
    subtitle: 'Feedback and requests directed to you',
  },
  {
    id: 'sent',
    label: 'Sent',
    title: 'Sent',
    subtitle: 'Feedback and requests you’ve sent to others',
  },
  {
    id: 'praise',
    label: 'Praise wall',
    title: 'Praise wall',
    subtitle: 'Public recognition across your team',
  },
];

const legacyTabs: Record<string, FeedbackTab> = {
  'my-feedback': 'for-you',
  'direct-reports': 'sent',
  'public-wall': 'praise',
};

const getPathTab = (pathname: string) => pathname.split('/')[2] || '';

const getActiveTab = (pathname: string): FeedbackTab => {
  const pathTab = getPathTab(pathname);
  if (pathTab in legacyTabs) return legacyTabs[pathTab];
  return feedbackTabs.some(({id}) => id === pathTab)
    ? (pathTab as FeedbackTab)
    : 'for-you';
};

const normaliseSidecarView = (
  value: string | null,
): FeedbackSidecarView | null => {
  if (!value) return null;
  const legacy: Record<string, FeedbackSidecarView> = {
    give: 'send-feedback',
    praise: 'post-praise',
    request: 'request-feedback',
  };
  const next = legacy[value] || value;
  return [
    'send-feedback',
    'post-praise',
    'request-feedback',
    'respond',
  ].includes(next)
    ? (next as FeedbackSidecarView)
    : null;
};

export const Feedback = observer(() => {
  const {
    authStore: {auth},
    feedbackStore: {managerFeedbackRequests = [], updateFeedbackRequest},
    storeDashboardRoutes: {activateDashboardTab},
  } = useStoreContext();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workspaceId = auth.user.workspace?.id || 'unknown-workspace';
  const activeTab = getActiveTab(location.pathname);
  const activeTabContent = feedbackTabs.find(({id}) => id === activeTab)!;
  const sidecarView = normaliseSidecarView(searchParams.get('action'));
  const requestId = searchParams.get('requestId') || searchParams.get('id');
  const selectedRequest = (managerFeedbackRequests as FeedbackRequest[]).find(
    ({id}) => id === requestId,
  );

  useEffect(() => {
    const pathTab = getPathTab(location.pathname);
    const shouldRedirect =
      location.pathname === '/feedback' ||
      location.pathname === '/feedback/' ||
      pathTab in legacyTabs;

    if (shouldRedirect) {
      const nextTab = legacyTabs[pathTab] || 'for-you';
      navigate(`/feedback/${nextTab}${location.search}`, {
        replace: true,
        state: location.state,
      });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    activateDashboardTab('feedback');
  }, [activateDashboardTab]);

  useEffect(() => {
    const getPendingRequests = async () => {
      const controller = new FeedbackController(new FeedbackRequestImpl());
      const response = await controller.getFeedbackRequests({
        reviewer: auth.user.id,
        include: 'user,reviewer',
      });
      updateFeedbackRequest(
        excludeDismissedFeedbackRequests(
          normaliseRequestResponse(response).filter(
            ({status}) => status !== 'completed',
          ),
          workspaceId,
          auth.user.id,
        ),
      );
    };

    getPendingRequests();
  }, [auth.user.id, updateFeedbackRequest, workspaceId]);

  const selectTab = useCallback(
    (tab: FeedbackTab) => {
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      next.delete('requestId');
      next.delete('id');
      const search = next.toString();
      navigate(`/feedback/${tab}${search ? `?${search}` : ''}`);
    },
    [navigate, searchParams],
  );

  const openSidecar = useCallback(
    (view: FeedbackSidecarView, request?: FeedbackRequest) => {
      const next = new URLSearchParams(searchParams);
      next.set('action', view);
      if (request) next.set('requestId', request.id);
      else next.delete('requestId');
      navigate(`${location.pathname}?${next.toString()}`, {
        state: request
          ? {
              user:
                typeof request.user === 'string'
                  ? request.user
                  : (request.user as any)?.id,
              subjectType: request.subjectType,
              subjectName: request.subjectName,
              subjectId: request.subjectId,
            }
          : location.state,
      });
    },
    [location.pathname, location.state, navigate, searchParams],
  );

  const closeSidecar = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    next.delete('requestId');
    next.delete('id');
    const search = next.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}`, {
      replace: true,
      state: null,
    });
  }, [location.pathname, navigate, searchParams]);

  const pageContent = useMemo(() => {
    if (activeTab === 'praise') return <PublicWall />;
    if (activeTab === 'sent') return <FeedBackDirectReports />;
    return (
      <MyFeedback onRespond={(request) => openSidecar('respond', request)} />
    );
  }, [activeTab, openSidecar]);

  return (
    <FeedbackPageWrapper>
      <FeaturePageHeader
        ariaLabel="Feedback views"
        tabs={feedbackTabs.map(({id, label}) => ({
          id,
          label,
          active: activeTab === id,
          onClick: () => selectTab(id),
        }))}
      />

      <FeaturePageContent>
        <FeedbackPageMain>
          <FeedbackPageHeadingRow>
            <FeedbackPageHeading>
              <h1>{activeTabContent.title}</h1>
              <p>{activeTabContent.subtitle}</p>
            </FeedbackPageHeading>
            <DropdownCustom
              collapseOnClick
              customDropdownWrapperStyles={{
                top: 'calc(100% + 8px)',
                right: 0,
                width: '196px',
                padding: '6px',
              }}
              customComponent={(handleOpen: () => void, open: boolean) => (
                <FeedbackCreateButton
                  type="button"
                  kind="primary"
                  aria-haspopup="menu"
                  aria-expanded={open}
                  onClick={handleOpen}>
                  Create
                  <FiChevronDown aria-hidden="true" />
                </FeedbackCreateButton>
              )}
              menu={(handleClose: () => void) => (
                <ActionMenu role="menu">
                  <ActionMenuItem
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      openSidecar('post-praise');
                      handleClose();
                    }}>
                    Post praise
                  </ActionMenuItem>
                  <ActionMenuItem
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      openSidecar('send-feedback');
                      handleClose();
                    }}>
                    Send feedback
                  </ActionMenuItem>
                  <ActionMenuItem
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      openSidecar('request-feedback');
                      handleClose();
                    }}>
                    Request feedback
                  </ActionMenuItem>
                </ActionMenu>
              )}
            />
          </FeedbackPageHeadingRow>
          {pageContent}
        </FeedbackPageMain>
      </FeaturePageContent>

      {sidecarView && (
        <FeedbackSidecar
          view={sidecarView}
          request={selectedRequest}
          requestId={requestId}
          onRequestCompleted={(completedRequestId) =>
            updateFeedbackRequest(
              (managerFeedbackRequests as FeedbackRequest[]).filter(
                ({id}) => id !== completedRequestId,
              ),
            )
          }
          onClose={closeSidecar}
        />
      )}
    </FeedbackPageWrapper>
  );
});
